import React, { useState } from "react";
import Header from "./header";
import Sidebar from "./sidebar";
import Footer from "./footer";
import { Navigate, useParams } from "react-router-dom";
import { hasKeys, isAdminRole } from "../shared/utils";
import VideoSearch from "../pages/video-finder/video-search";
import ReplaceSearch from "../pages/video-finder/replace-search-component";
import SavedListTable from "../pages/video-finder/saved-video-list-table";
import Settings from "../pages/video-finder/settings";
import VideoGuideDefaultSettings from "../pages/video-guide/video-guide-default-settings";
import VideoGuideSettings from "../pages/video-guide/video-guide-settings";
import ScribeContentGenerate from "../pages/scribe/scribe-content-generate";
import ScribeContents from "../pages/scribe/scribe-contents";
import SummaryGeneration from "../pages/video-finder/summary-generation";
import VideoGuideReport from "../pages/video-guide/video-guide-report";
import CourseContents from "../pages/course-content-table";
import ScribeBotSettings from "../pages/scribe/scribe-settings";
import PrivatePage from "./private-page";
import TranscriptGeneration from "../pages/video-finder/transcript-generation";
import PingingVideo from "../pages/pinging/PingingVideo";
import PingingWeb from "../pages/pinging/PingingWeb";
import AssignPingingVideo from "../pages/assignments/AssignPingingVideo";
import AssignPingingWeb from "../pages/assignments/AssignPingingWeb";
import AssignmentContent from "../pages/contents/AssignmentContent";
import CourseContent from "../pages/contents/CourseContent";
import RoleManagement from "../pages/role-management/RoleManagement";
import StandardsWizard from "../pages/standards-wizard/StandardsWizard";
import SavedCourses from "../pages/standards-wizard/SavedCourses";
import { AccessProvider, useAccess } from "../contexts/AccessContext";

export const routeToolMap = {
  "search": 1,                  // Video Finder
  "replace-search": 1,          // Video Finder (same tool)
  "videos": 1,                  // Video Finder saved list
  "settings": 1,  
  "summary-generation": 1,
  "transcript-generation": 1,

  "video-guide-default-settings": 2,  // Video Guide
  "video-guide-settings": 2,          // Video Guide
  "video-guide-report": 2,                       // if it's Video Guide settings

  "scribe-content-generate": 3,  // Scribe AI Content
  "scribe-contents": 3,
  "scribe-settings": 3,

  "pinging-video": 4,            // Pinging Video
  "assign-pinging-video": 6,     // Assign Pinging Video

  "pinging-web": 5,              // Pinging Web
  "assign-pinging-web": 7,       // Assign Pinging Web

  "assignment-content": 8,       // Assignment Contents
  "course-content": 9,           // Course Contents
  "standards-wizard": 10,        // Standards Wizard
  "saved-courses": 10,           // Saved Courses (same tool)

  "role-management": null,       // Always admin-only
};

const editProtectedRoutes = [
  "settings",
  "video-guide-default-settings",
  "scribe-settings"
];


const PageContent = ({ action, params, generateKey, transcriptKey }) => {
  const { hasView, hasEdit } = useAccess();

  // Old logic restored:
  if (!hasKeys(params) || !hasKeys(params.action) || !action) {
    return null;
  }

  const toolId = routeToolMap[action];
  const isAdmin = isAdminRole();
  // 🚨 Determine if this route requires EDIT access
  const requireEdit = editProtectedRoutes.includes(action);

   // 🚨 Final access logic
  const allowed = isAdmin
    ? true                                   // Admin → always allowed
    : requireEdit
    ? hasEdit(toolId)                        // For settings pages → need edit
    : hasView(toolId);                       // For other pages → need view

// If NOT allowed → redirect to homepage
  if (!allowed) {
    return <Navigate to="/home" replace />;
  }

  switch (action) {
    case "role-management":
      return <RoleManagement />;
    case "search":
      return <VideoSearch />;
    case "replace-search":
      return <ReplaceSearch />;
    case "videos":
      return <SavedListTable />;
    case "settings":
      return <Settings />;
    case "summary-generation":
      return <SummaryGeneration />;
    case "transcript-generation":
      return <TranscriptGeneration key={transcriptKey} />;
    case "video-guide-default-settings":
      return <VideoGuideDefaultSettings />;
    case "video-guide-settings":
      return <VideoGuideSettings />;
    case "video-guide-report":
      return <VideoGuideReport />;
    case "scribe-content-generate":
      return <ScribeContentGenerate key={generateKey} />;
    case "scribe-contents":
      return <ScribeContents />;
    case "scribe-settings":
      return <ScribeBotSettings />;
    case "pinging-video":
      return <PingingVideo />;
    case "pinging-web":
      return <PingingWeb />;
    case "assign-pinging-video":
      return <AssignPingingVideo />;
    case "assign-pinging-web":
      return <AssignPingingWeb />;
    case "assignment-content":
      return <AssignmentContent />;
    case "course-content":
      return <CourseContent />;
    case "standards-wizard":
      return <StandardsWizard />;
    case "saved-courses":
      return <SavedCourses />;
    default:
      return null;
  }
};



const PageLayout = (props) => {
  const { location } = props;
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth <= 768 ? false : true);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768 ? true : false);
  const [generateKey, setGenerateKey] = useState(0);
  const [transcriptKey, setTranscriptKey] = useState(0);

  // get current user id from your auth/session store; replace as needed
  const currentUserId = (() => {
    const raw = localStorage.getItem("user_id");
    if (!raw) return null;
    const parsed = Number(raw);
    return isNaN(parsed) ? null : parsed;
  })();

  const resetTranscriptComponent = () => {
    setTranscriptKey((prev) => prev + 1);
  };

  const resetGenerateComponent = () => {
    setGenerateKey((prev) => prev + 1);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  const params = useParams();
  let divStyle = {};
  let className = "";
  if (location.pathname === "/home") {
    divStyle = {
      minHeight: "calc(100vh - 64px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    };
    className = "home-bg";
  } else {
    divStyle = {
      // padding: '4px 4px 5px', // Adjust padding as needed
      // maxWidth: '700px', // Set the desired height
      // height:'100%',
      // width:'100%',
      // backgroundSize: '99%', // Adjust the zoom level by changing the percentage
      // backgroundPosition: 'center',
      // backgroundRepeat: 'no-repeat',
      paddingBottom: "40px",
    };
    className = "main-div";
  }

  const searchParams = new URLSearchParams(location.search);
  const queryParams = {};
  for (let [key, value] of searchParams.entries()) {
    queryParams[key] = value;
  }

  const combinedParams = { ...params, ...queryParams };

   return (
    <div className="app-container">
      <AccessProvider userId={currentUserId}>
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          toggleSidebar={toggleSidebar}
        />
        <Sidebar
          params={combinedParams}
          location={location}
          isOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          setIsSidebarOpen={setIsSidebarOpen}
          setIsMobileView={setIsMobileView}
          isMobileView={isMobileView}
          onGenerateReset={resetGenerateComponent}
          onTranscriptReset={resetTranscriptComponent}
        />
        <div className={`main-content ${isSidebarOpen ? "sidebar-open" : "sidebar-close"}`}>
          <div className={className} style={divStyle}>
            {!hasKeys(params.action) && (
              <div style={{ width: "70%" }}>
                <img className="img-fluid" src={"/images/admin-bg.jpeg"} alt="home" />
              </div>
            )}
            {hasKeys(params.action) && (
              <PrivatePage>
                <PageContent
                  action={params.action}
                  params={combinedParams}
                  generateKey={generateKey}
                  transcriptKey={transcriptKey}
                />
              </PrivatePage>
            )}
          </div>
        </div>
        <Footer isOpen={isSidebarOpen} />
      </AccessProvider>
    </div>
  );
};

export default PageLayout;
